/**
 * Central game configuration — adjust reels, symbols, timing, and payouts here.
 */

/**
 * 8px grid — spacing, borders, radii, type (single source for all chrome).
 */
const ui = {
  panelPadX: 20,
  panelPadY: 14,
  /** Tallest stat card row; must fit label + inline bet chips + padding */
  statRowH: 76,
  rowGap: 12,
  cardGap: 10,
  cardPadX: 12,
  cardPadY: 10,
  /** Horizontal gap between stat label and value (BALANCE 12,345) */
  statInlineGap: 10,
  /** Vertical gap between BALANCE row and TOTAL BET row */
  statStackGap: 4,
  /** Extra letter-spacing on stat value numbers */
  statValueTracking: 0.4,
  /**
   * LAST WIN amount vertical placement in its column: fraction from label bottom toward inner bottom.
   * 0.5 = centered in band; higher = lower on screen.
   */
  lastWinVerticalBias: 0.62,
  /** Space between LAST WIN card and SPIN button */
  spinGap: 14,
  spinW: 160,
  spinH: 70,

  radiusPanel: 20,
  radiusCard: 14,
  radiusChip: 14,
  radiusSpin: 18,
  radiusWell: 12,
  radiusCabinet: 16,
  radiusColumn: 10,

  /** Border widths (px) — use everywhere for consistency */
  strokePanelOuter: 1,
  strokePanelInner: 1,
  strokeCardOuter: 2,
  strokeCardInner: 1,
  strokeChip: 2,
  strokeChipInner: 1,
  strokeSpinOuter: 3,
  strokeSpinInner: 1,
  strokeCabinetOuter: 3,
  strokeCabinetMid: 2,
  strokeCabinetInner: 1,
  strokeWell: 2,
  strokeWellInner: 1,
  strokeHeaderRule: 2,

  typeStatLabel: 10,
  typeStatValue: 20,
  /** LAST WIN card — main total (larger) */
  typeWinLastTotal: 22,
  /** Per-line amounts only, under total */
  typeWinDetail: 10,
  typeSpin: 26,
  typeBetGlyph: 22,
  typeHeaderTitle: 36,
  typeHeaderSub: 11,
  /** Header — prominent last win amount (right side) */
  typeHeaderWin: 30,

  /** Base art width for ± chips; row layout scales to fill card */
  betChipW: 46,
  betChipH: 44,
  betChipGap: 8,
  betChipMinW: 28,
  /** Target share of row width per ± button before middle field (0–0.5) */
  betChipSideRatio: 0.2,
  betInputMinW: 36,
  /** Vertical gap between stat label and ± chip row */
  betLabelChipGap: 10,

  /** Win line overlay on the reel grid */
  winLineGlowWidth: 10,
  winLineCoreWidth: 3,
  winLineJointRadius: 5,
  winLineLabelOffsetY: 32,
  /** Per-cell frame while a payline is shown (full line, all reels) */
  winLineCellBorderInset: 5,
  winLineCellBorderRadius: 10,
  winLineCellBorderWidth: 5,
  winLineCellBorderDimWidth: 3.5,
  winLineCellBorderDimAlpha: 1,
  winLineCellBorderGlowAlpha: 0.82,
  /** Tinted fill behind payline cells (strong = winning cell, dim = path-only) */
  winLineCellBgAlpha: 0.32,
  winLineCellBgDimAlpha: 0.16,
  /** Alpha swing added on top of base during the idle pulse */
  winLineCellBgPulseSwing: 0.14,

  gapCabinetToBar: 20,
  gapBarBottom: 16,
  /** Pixel inset of reel well around the symbol grid */
  wellInset: 6,
  /**
   * Extra pixels clipped off the bottom of the reel viewport mask so spinning symbols
   * disappear under the frame instead of drawing past it.
   */
  reelViewportMaskBottomExtra: 16,
  /**
   * Extra top clip on the reel viewport when reels are stopped — hides the strip symbol
   * peeking above row 0. Not applied while spinning.
   */
  reelViewportMaskTopExtraWhenIdle: 14,
  /**
   * Reel cabinet scale factor after the “fit to viewport” pass. 1 = max fit; below 1 = smaller grid (bottom bar unchanged).
   */
  slotCabinetScaleMul: 0.92,
};

export const gameConfig = {
  /** Logical grid */
  reelCount: 5,
  rowCount: 3,

  /** Layout — sized for fullscreen scale-up (portrait cabinet) */
  cellWidth: 177,
  cellHeight: 177,
  reelGap: 0,
  machinePadding: 24,
  headerHeight: 84,
  /**
   * Bottom panel: one row (stats + inline bet + SPIN). Keeps in sync with ui padding
   * and the taller of the stat cards vs. the spin button.
   */
  controlBarHeight: ui.panelPadY * 2 + Math.max(ui.statRowH, ui.spinH),
  /** Marble column width on each side of the reel window */
  columnWidth: 48,

  ui,

  /** Strip length tuned down to reduce hidden Spine workload and startup hitches. */
  stripMinLength: 64,
  stripLandingOffsetMin: 7,
  stripLandingOffsetSpread: 4,
  /** Less strip travel at the same cruise px/s → shorter tier-1 spin without slowing scroll speed. */
  extraSpinCellsMin: 11,
  extraSpinCellsSpread: 5,

  spinPhaseAccelerate: 0,
  spinPhaseCruise: 0.26,
  /** Shorter final ease only; cruise velocity unchanged (see reelCruisePixelsPerSecond). */
  spinPhaseDecelerate: 0.22,
  /** +1 = current direction, -1 = opposite direction */
  reelSpinDirection: -1,
  /**
   * During a spin, only strip indices within roughly the visible window ± this many cells
   * keep live Spine instances (`Symbol#setActive`). Same for normal and Quick Spin — idle cruise
   * (Blazing Sevens) needs only a small pad; fewer off-screen rigs updating.
   */
  reelSpinViewportPadCells: 2,
  /** @deprecated Same as {@link reelSpinViewportPadCells} — kept for older call sites. */
  turboReelSpinViewportPadCells: 2,
  /** Extra seconds before each next column lands (all columns start together; only stops stagger L→R) */
  reelStopStaggerSec: 0.34,
  /**
   * Linear strip speed during the cruise phase (px/s), same for every reel.
   * Staggered stops add extra strip travel per column so decel starts later — same speed, not stretched time.
   * Locked to the former derived cap so tightening travel above does not reduce scroll speed.
   */
  reelCruisePixelsPerSecond: 3400,
  /** Cap when deriving cruise speed from spinPhaseCruise + typical travel (avoids huge stagger strip growth). */
  reelCruiseDerivedCapPxPerSec: 3400,
  /**
   * Bet-speed tier 2 (two arrows lit): multiplies `reelStopStaggerSec` only — tighter spacing between reel stops.
   * Scroll/decel speed stays identical to tier 1. Use 1 for same timing as tier 1; lower = stops closer together in time.
   */
  betSpeedTier2StaggerScale: 0.48,
  /** After tier-2 spin lands, brief pause before expanding-wild / result flow (ms). */
  betSpeedTier2ResultHoldMs: 400,
  /** If tier-2 win needs extra time before next phase (ms). */
  betSpeedTier2PostWinPadMs: 500,
  /**
   * Legacy min wait before seek-stop (hand-stop during API wait / older path).
   * Tier-3 Quick Spin uses a dedicated short timeline (`turboMode`) instead of wait+seek.
   */
  turboSnapMinSpinMs: 480,
  /** GSAP timeScale for decel + bounce after turbo/hand-stop seeks to the land label. */
  turboLandTimeScale: 1.85,
  /**
   * After Quick Spin / hold-turbo lands, tiny pause before expanding-wild / result flow (ms).
   * Keep low so manual re-spins stay snappy.
   */
  turboSnapResultHoldMs: 40,
  /** Extra result hold on Android / low-memory (ms); stacked on {@link turboSnapResultHoldMs}. */
  androidTurboResultHoldMs: 20,
  /** Autoplay delay between Quick Spin rounds (ms). Hold-turbo uses {@link turboHoldAutoplayDelayMs}. */
  turboAutoplayDelayMs: 80,
  /** Hold-turbo autoplay inter-spin delay (ms). */
  turboHoldAutoplayDelayMs: 100,
  /** Manual re-spin cooldown after any spin ends (ms). */
  postSpinRespinCooldownMs: 50,
  /** Android / low-memory manual re-spin cooldown floor (ms). */
  androidPostSpinRespinCooldownMs: 60,
  /**
   * Quick Spin strip travel — short but readable (industry Quick Spin ~0.45–0.55s).
   * All reels share one random base; micro-stagger adds a few cells per column.
   * All spin tiers use idle Spine only (Blazing Sevens) — scroll motion provides the blur read.
   */
  turboStripLandingOffsetMin: 4,
  turboStripLandingOffsetSpread: 2,
  turboExtraSpinCellsMin: 3,
  turboExtraSpinCellsSpread: 2,
  /**
   * Micro L→R stop stagger for Quick Spin (sec). ~1–2 frames at 60fps — feels cascading
   * without the simultaneous 5-reel Spine settle freeze on Android WebViews.
   */
  turboReelStopStaggerSec: 0.028,
  /** When hand-stopping a normal spin, delay each next reel’s seek-stop by this many frames. */
  handStopStaggerFrames: 2,
  /** Turbo win celebration: min seconds per buildup tier (replaces {@link winAnimationMinDisplaySec}). */
  winAnimationMinDisplaySecTurbo: 0.9,
  /** Spine timeScale during turbo celebration buildup. */
  turboCelebrationTimeScale: 1.4,
  /** Expanding-wild reveal duration scale under Quick Spin / hold-turbo. */
  turboExpandingWildExpandScale: 0.45,
  /** Expanding-wild hold duration scale under Quick Spin / hold-turbo. */
  turboExpandingWildPlayScale: 0.4,
  /** Spine playback scale for expanding wild under turbo. */
  turboExpandingWildSpineTimeScale: 2.8,
  /** Minimum time reels stay in motion after a result arrives (ms). Stops “instant grid” when the player hit stop during the round-trip. */
  minReelSpinAnimationMs: 500,
  /**
   * Win celebration Spine: when true, tier gates are **1× / 2× / 5× / 10×** (big→huge→mega→epic) and the overlay can show from **1×** (QA only).
   * When false, production gates use {@link winCelebrationTierMultiples} (big / huge / mega / epic).
   */
  winCelebrationTestTiers: false,
  /**
   * Production win÷stake thresholds. Tier ranges: big [big, huge), huge [huge, mega), mega [mega, epic), epic ≥ epic.
   * @type {{ big: number, huge: number, mega: number, epic: number }}
   */
  winCelebrationTierMultiples: {
    big: 20,
    huge: 30,
    mega: 40,
    epic: 50,
  },
  /**
   * Minimum seconds each payline win stays on screen (playback speed unchanged; symbol `win` loops).
   * Intro clips play once; short intros hold on their idle loop until this minimum.
   * Transition clips (e.g. `big_win_end`) are not padded.
   */
  winAnimationMinDisplaySec: 3,
  /**
   * Last-win count-up when there are no payline hits (wins below celebration threshold).
   * When paylines win, duration is `hitCount × winAnimationMinDisplaySec`.
   */
  lastWinRollupDurationSec: 3,
  cruiseSpeedFactor: 22,

  startingBalance: 10_000,
  defaultBet: 10,
  minBet: 1,
  maxBet: 500,
  /** Max win multiplier from InitialState (maxOdd); shown to players as e.g. 5000x. */
  maxOdd: null,
  /** Return to player — shown on start screen and in the info tab. */
  rtpPercent: 96.34,

  /**
   * Symbol definitions with Spine animation mappings.
   *
   * Each symbol must have a 'spine' property that matches a .json file in /public/assets/spines/
   * Available spine files: 10, J, Q, K, A, bug, pyramides, sphinx, mummy, cleopatra, pharaon
   *
   * Every spine exports `idle`, `win`, and `noWin` animations driven by RealSpineAdapter.
   *
   * Symbol id 9 is the expanding WILD, 10 is the coin scatter, 11 is the star scatter.
   */
  symbols: [
    { id: 1, label: '1', color: 0xc4a574, spine: 'bug', payoutMultipliers: [1, 5, 25, 500] },
    { id: 2, label: '2', color: 0x8b6914, spine: 'sphinx', payoutMultipliers: [0, 4, 12, 70] },
    { id: 3, label: '3', color: 0xd4af37, spine: 'mummy', payoutMultipliers: [0, 4, 12, 70] },
    { id: 4, label: '4', color: 0xffd447, spine: 'A', payoutMultipliers: [0, 2, 4, 20] },
    { id: 5, label: '5', color: 0xff6b9d, spine: 'K', payoutMultipliers: [0, 1, 3, 15] },
    { id: 6, label: '6', color: 0xe8a317, spine: 'Q', payoutMultipliers: [0, 1, 3, 15] },
    { id: 7, label: '7', color: 0x2eb87a, spine: 'J', payoutMultipliers: [0, 1, 3, 15] },
    { id: 8, label: '8', color: 0x3b6fd9, spine: '10', payoutMultipliers: [0, 1, 3, 15] },
    { id: 9, label: '9', color: 0xe7c463, spine: 'pharaon' },
    {
      id: 10,
      label: '10',
      color: 0xf4d03f,
      spine: 'cleopatra',
      scatterPays: { 3: 5, 4: 20, 5: 100 },
    },
    {
      id: 11,
      label: '11',
      color: 0xc9a227,
      spine: 'pyramides',
      scatterPays: { 3: 20 },
    },
  ],

  /**
   * Default 10 paylines for the live 5×3 title (row index 0 = top, 2 = bottom).
   * The 40-line and 100-line launches replace this array at session start.
   */
  paylines: [
    [1, 1, 1, 1, 1], // 1
    [0, 0, 0, 0, 0], // 2
    [2, 2, 2, 2, 2], // 3
    [0, 1, 2, 1, 0], // 4
    [2, 1, 0, 1, 2], // 5
    [0, 0, 1, 2, 2], // 6
    [2, 2, 1, 0, 0], // 7
    [1, 2, 2, 2, 1], // 8
    [1, 0, 0, 0, 1], // 9
    [0, 1, 1, 1, 0], // 10
  ],

  /** Stage clear color (fallback) */
  backgroundColor: 0x0a0604,
  machineFrameColor: 0x1a0f0a,
  reelMaskAlpha: 0.08,

  /**
   * Ancient Egypt — warm gold, sandstone, and deep tomb browns.
   */
  theme: {
    fontDisplay: '"Syne", system-ui, sans-serif',
    fontUi: '"Montserrat", system-ui, -apple-system, sans-serif',
    /** Symbol tile inset (matches textureFactory pad) */
    symbolTexturePad: 0,
    /** Primary accent (borders, chips, highlights) */
    gold: 0xe7c463,
    goldBright: 0xfff8dc,
    goldDim: 0x8b6914,
    lightning: 0xf5e6c8,
    chrome: 0xc4a574,
    marble: 0xd4b896,
    marbleShadow: 0x5c4033,
    /** Control bar — warm glass stack */
    panelRimHi: 0x4a3020,
    panelDeep: 0x1a0f0a,
    panelMid: 0x2c1810,
    panelBorder: 0x6b4423,
    panelGlass: 0x3d2914,
    panelShadow: 0x0d0705,
    /** Stat card surfaces (unused when cards hidden; kept for tools / future) */
    cardSurfaceHi: 0x3d2914,
    cardSurfaceMid: 0x1a0f0a,
    cardSurfaceLo: 0x0d0705,
    cardHeroHi: 0x4a3020,
    cardHeroMid: 0x2c1810,
    cardHeroLo: 0x0d0705,
    /** ± chip button */
    chipFaceHi: 0x5c4033,
    chipFaceMid: 0x3d2914,
    chipFaceLo: 0x1a0f0a,
    reelWell: 0x0a0604,
    /** Multiply tint on symbols not part of the current win line */
    reelSymbolDimTint: 0x6b5344,
    reelInnerLine: 0x5c4033,
    headerShine: 0x4a3020,
    skyGlow: 0xe7c463,
    /** Radial backdrop (GameApp stage gradient) */
    ambientGlow: 0xd4af37,
    cloudTint: 0x8b7355,
    spinCore: 0x9a7b0a,
    spinCoreHi: 0xe7c463,
    spinCoreLo: 0x4a3020,
    spinRing: 0xf4d03f,
    /** Typography on chrome */
    textLabel: 0xc4a574,
    textMuted: 0x9a7b4f,
    textBright: 0xfff8dc,
    /** Bright money figures on dark/gold HUD (balance, last win, chip amounts) */
    hudValue: 0xffe566,
    /** FUN / currency tags on HUD and chips */
    hudCurrency: 0xfff0a8,
    /** Drop shadow behind HUD figures — dark brown, not gold (gold glow disappears on gold UI) */
    hudValueShadow: 0x1a0800,
    balanceGlow: 0x1a0800,
    balanceFigure: 0xffe566,
    winGlow: 0xffe566,
    danger: 0xc0392b,
    /** HUD figures (balance, wins, selected chips) — alias of {@link hudValue} */
    valueAccent: 0xffe566,
    /** Currency labels — alias of {@link hudCurrency} */
    currencyAccent: 0xfff0a8,
    /** Modal chrome — warm sand / papyrus panels, pharaoh gold trim, ochre accents */
    modalBorderDark: 0x2a2018,
    modalBorderGold: 0xc9a227,
    modalBorderGoldHi: 0xf5e6a8,
    modalBorderGoldLo: 0x7a5530,
    modalButtonFill: 0xa67c3a,
    modalButtonFillAlt: 0x6b4423,
    modalSurface: 0x4a3b2a,
    modalSurfaceHi: 0x6b5640,
    modalStroke: 0x9a8268,
    modalAccent: 0xc9922a,
    modalAccentHi: 0xe7c463,
    /** High-contrast titles on sand panels */
    modalHeading: 0xffffff,
    /** Primary body copy on sand panels */
    modalBody: 0xfff0d4,
    /** Secondary labels (stats, slider bounds, inactive tabs) */
    modalBodyMuted: 0xe8dcc8,
    modalTabActive: 0xffe566,
    modalTabInactive: 0xc8b898,
    modalDividerMid: 0xc9a227,
    modalDim: 0x1a1208,
    modalDanger: 0xc75032,
    modalSwitchOffMid: 0x5c4a38,
    modalSwitchOffLo: 0x3d2f22,
    captionMuted: 0xe0d0b0,
    badgeAccent: 0xe7c463,
    /** Payline strokes (info / guide overlay) */
    paylineColors: [
      0xf4d03f,
      0xe7c463,
      0xd4af37,
      0xc9a227,
      0xf5e6c8,
      0xb8860b,
      0xcd853f,
      0xdaa520,
      0x8b6914,
      0xfff8dc,
    ],
    /** Expanding-wild full-column backdrop (radial gradient stops) */
    expandingWildColumnBgHi: 0xfff0a8,
    expandingWildColumnBgMid: 0xc9922a,
    expandingWildColumnBg: 0x2a1808,
    /** Win-line cell borders — vivid high-luminance hues */
    winLineBorderColors: [
      0x00e5ff,
      0xff4081,
      0x69f0ae,
      0x536dfe,
      0xb388ff,
      0xff5252,
      0x1de9b6,
      0xe040fb,
      0x00e676,
      0x448aff,
    ],
  },

  /**
   * Viewport tuning — applied in GameApp `layout()` (uniform Pixi `world` scale).
   */
  viewport: {
    /**
     * Multiplies world scale on **landscape** tablets only. `1` disables.
     * Slightly below 1 shrinks the whole game uniformly so the corner chrome icons
     * (home / sound / menu / fullscreen) gain a margin from the screen edges and
     * don't sit flush against them. Lower = more margin.
     * Portrait tablets use the mobile view (no separate portrait-tablet scale).
     */
    tabletLandscapeWorldScaleMul: 0.92,
    /**
     * Canvas bottom strip only (balance / quick bets / icons): extra Y for landscape tablets.
     * Positive = move that strip lower on screen. Does not affect reel cabinet or spin beside reels.
     */
    tabletBottomMenuOffsetY: 80,

    /** Landscape tablet: push corner icons slightly lower for air under the QB strip row. */
    tabletLandscapeBottomIconsGapExtra: 12,
    /** Landscape tablet: pull corner icons inward toward the balance / LAST WIN panels (logical px). */
    tabletLandscapeBottomIconsTowardCenterPx: 28,

    /**
     * Desktop landscape only (not tablet / phone): pull the four corner icons
     * (home / fullscreen on the left, sound / menu on the right) inward toward
     * the balance / LAST WIN panels so they keep a gap from the screen corners.
     * Positive = closer to the horizontal center.
     */
    desktopBottomIconsTowardCenterPx: 56,

    /**
     * Desktop browser fullscreen only ({@link GameApp#_isDesktopFullscreen}):
     * extra uniform shrink on the Pixi `world` so the bottom HUD strip and corner
     * icons stay inside the 1922×1080 stage. `1` disables.
     */
    desktopFullscreenWorldScaleMul: 0.90,

    /**
     * Landscape tablet (`_isTabletViewport`): multiplier on win celebration.
     * Below 1 = smaller on tablets.
     */
    tabletWinCelebrationScaleMul: 0.85,
    /**
     * Portrait mobile (phone + tablet): extra uniform scale on the reel cabinet after the
     * width-fill solve in {@link GameApp#_applyMobileGameVisualScale}. `1` = edge-to-edge;
     * above `1` enlarges symbols (slight edge bleed). HUD position follows automatically.
     * Live 5×3 stays at 1.20. 40-line, 100-line, and buy bonus use the smaller value so the
     * top frame and logo stay on screen.
     */
    mobileGameVisualScaleMul: 1.20,
    compactMobileGameVisualScaleMul: 1.05,
    /** Portrait mobile: multiplier on the lerped celebration scale. */
    narrowPortraitWinCelebrationMul: 0.88,
    /**
     * Desktop / large landscape (not tablet, not portrait mobile): extra celebration size. `1` = default.
     */
    desktopWinCelebrationScaleMul: 1,
    /** Landscape tablet: move win celebration root down in logical 1922×1080 space (positive = lower). */
    tabletWinCelebrationOffsetYPx: 60,
  },

  animations: {
    idle: 'idle',
    win: 'win',
    noWin: 'noWin',
    /** Reel spin; falls back to {@link gameConfig.animations.spinningLegacy} if missing in skeleton */
    spinning: 'spinning',
    /** Common export name when the clip is not named `spinning` */
    spinningLegacy: 'blur',
    press: 'press',
    release: 'release',
  },

  adapter: {
    /** 'pulse' = very subtle idle breathing on symbols */
    symbolIdleMode: 'pulse',
    idlePulseScale: 1.012,
    idlePulseDuration: 2.75,
    /** Win loop: pop → wiggle → settle → light shimmer */
    winScalePeak: 1.16,
    winPopDuration: 0.27,
    winSettleDuration: 0.38,
    winRotationWiggleRad: 0.062,
    winShimmerAlphaMin: 0.9,
    /** Delay between symbols when a line wins (seconds, left-to-right along cells). 0 = all start in sync. */
    winStaggerSec: 0,
    /** Minimum seconds each payline stays on screen while cycling (actual hold ≥ Spine `win` clip length). */
    winLinePhaseHoldSec: 3,
    /** Fire spritesheet behind winning cells during payline animation */
    winFireAnimSpeed: 0.2,
    winFireAlpha: 0.9,
    /** Horizontal / vertical fit vs cell size (fraction of cell width/height). */
    winFireWidthPad: 0.92,
    winFireHeightPad: 2,
    /** Anchor Y (1 = bottom); offset pushes fire down from cell center (px). */
    winFireAnchorY: 0.96,
    winFireOffsetY: 75,
    /** Payline cell backdrop pulse (seconds, yoyo loop) */
    winLineBgPulseSec: 1.35,
    /** Expanding-wild column FX — spine (`pharaon_extanded`). */
    expandingWildColumnBgAlpha: 0.88,
    expandingWildColumnBgRadius: 12,
    expandingWildColumnBgPulseSec: 0.9,
    expandingWildColumnBgPulseSwing: 0.22,
    /** Vertical reveal from center (sec); wide mask — no horizontal clip. */
    expandingWildExpandSec: 0.65,
    /** Initial visible height as fraction of full column (0.33 = one row; higher = more shown). */
    expandingWildRevealStartRatio: 0.42,
    expandingWildPlaySec: 1,
    /** Spine animation name after vertical reveal (`pharaon_extanded.json`). */
    expandingWildSpineAnimation: 'win',
    /** Spine `win` clip playback only (> 1 = faster frames, does not affect reveal timing). */
    expandingWildSpineTimeScale: 2,
    /** Uniform scale vs 3-row column height (1 = exact column; >1 = taller). */
    expandingWildScalePad: 1,
    /** Horizontal squeeze (< 1 = narrower width, height unchanged). */
    expandingWildWidthScale: 0.92,
    /** Buy-bonus multiplier on the expanded wild, as a fraction of reel width. */
    expandingWildMultiplierWidthRatio: 0.92,
    /** @deprecated Unused — all wild columns share one synchronized timeline. */
    expandingWildReelStaggerSec: 0,
    buttonPressScale: 1,
    buttonTweenDuration: 0.12,
  },

  /**
   * Client-only presentation FX (anticipation, juice, particles). No backend impact.
   */
  visualFx: {
    anticipationMinScatters: 2,
    /** 0-based reels where star scatter (11) can appear — 1st, 3rd, 5th columns. */
    anticipationStarScatterReels: [0, 2, 4],
    anticipationExtraCellsMin: 6,
    anticipationExtraCellsSpread: 4,
    anticipationCruiseSlowFactor: 1.55,
    anticipationPulseSec: 0.42,
    anticipationColumnPadX: 10,
    anticipationColumnPadY: 8,
    anticipationColumnRadius: 14,
    landPopSymbolIds: [9, 10, 11],
    landPopScalePeak: 1.18,
    landPopDuration: 0.22,
    dimSymbolAlpha: 0.5,
    paylineEnergyDuration: 0.38,
    /** First winning line: skip blocking energy trail so the win reads instantly. */
    paylineEnergySkipFirstLine: true,
    /** When first-line skip is off, use this shorter trail (seconds). */
    paylineEnergyFirstLineDuration: 0.14,
    /** Symbol win stagger on the first payline only (0 = all cells pop together). */
    winStaggerFirstLineSec: 0,
    paylineEnergyDotRadius: 9,
    paylineEnergyTrailWidth: 6,
    celebrationFlashAlpha: 0.32,
    celebrationFlashDuration: 0.12,
    /** Seconds of cabinet rumble / shake before the first big-win popup appears.
     *  Fallback only — runtime uses {@link sound.getDurationSec}(`bigWinAlert`). */
    celebrationAnticipationSec: 0.9,
    celebrationShakeIntensity: 7,
    celebrationShakeDuration: 0.38,
    celebrationGlowPulseSec: 0.9,
    celebrationGlowPulseSwing: 0.22,
    expandingWildFlashAlpha: 0.22,
    expandingWildShakeIntensity: 5,
    particleBurstCount: 14,
    particleBurstSpread: 52,
    particleBurstDuration: 0.58,
    particleBurstUpwardBias: 18,
    collectCoinCount: 24,
    idleGlowPulseSec: 3.2,
    idleGlowPulseSwing: 0.12,
    idleLogoFloatPx: 4,
    idleLogoFloatSec: 2.8,
    spinIdleRingRadius: 78,
    spinIdleRingRotateSec: 8,
    ambientMoteIntervalMs: 420,
    ambientMoteChance: 0.55,
    reelWellDimAlpha: 0.38,
    winFireAlphaSwing: 0.22,
  },

  /** Fraction of cell height — roll past the lock line before easing back (larger = longer arc). */
  reelLandBounceRatio: 0.36,
  /** Min seconds for that overshoot deceleration segment (tier speed scales it). */
  reelLandBounceIn: 0.05,
  /** Seconds for the snap-back to aligned stop; capped vs spinPhaseDecelerate budget. */
  reelLandBounceOut: 0.10,

  spinDistanceRatios: {
    accelerate: 0,
    cruise: 0.7,
    decelerate: 0.3,
  },

  debug: {
    defaultEnabled: false,
    showCellIndices: true,
    /* Opening photo layout — dormant. See src/debug/openingPhotoLayout.js + GameApp hooks.
    openingPhotoLayout: {
      enabled: true,
      hud: {
        balance: 650,
        bet: 10,
        currencyCode: 'EUR',
        currencySymbol: '€',
        currencyPrecision: 2,
        maxOddFallback: 5000,
      },
    },
    */
  },
};

/** Live 5×3 layout — restore when leaving a 40- or 100-line launch. */
const DEFAULT_ROW_COUNT = 3;
const DEFAULT_CELL_WIDTH = 177;
const DEFAULT_CELL_HEIGHT = 177;
const DEFAULT_PAYLINES = gameConfig.paylines.map((line) => line.slice());

/**
 * Apply launch layout onto THIS module's `gameConfig` (must live here so Vite HMR
 * cannot leave GameApp holding a different config object than a variants helper).
 *
 * 40-line and 100-line: 5×4 with cells scaled so block height ≈ live 5×3 — mobile
 * frame/HUD structure stays aligned with the regular title.
 *
 * @param {{
 *   lines40?: boolean,
 *   lines100?: boolean,
 *   rowCount?: number,
 *   paylines?: number[][] | null,
 * }} variant
 */
export function applyLaunchLayout(variant) {
  if (!variant) return;

  if (variant.lines40 || variant.lines100) {
    gameConfig.rowCount = 4;
    if (Array.isArray(variant.paylines) && variant.paylines.length) {
      gameConfig.paylines = variant.paylines;
    }
    const cellH = Math.round((DEFAULT_ROW_COUNT * DEFAULT_CELL_HEIGHT) / 4);
    const cellW = Math.round(DEFAULT_CELL_WIDTH * (cellH / DEFAULT_CELL_HEIGHT));
    gameConfig.cellHeight = cellH;
    gameConfig.cellWidth = cellW;
    return;
  }

  gameConfig.rowCount =
    Number.isFinite(variant.rowCount) && variant.rowCount > 0
      ? variant.rowCount
      : DEFAULT_ROW_COUNT;
  gameConfig.paylines =
    Array.isArray(variant.paylines) && variant.paylines.length
      ? variant.paylines
      : DEFAULT_PAYLINES.map((line) => line.slice());
  gameConfig.cellHeight = DEFAULT_CELL_HEIGHT;
  gameConfig.cellWidth = DEFAULT_CELL_WIDTH;
}

/**
 * Bottom-bar win amount count-up duration (regular wins, not celebration overlay).
 * One full payline cycle: each line held at least {@link gameConfig.winAnimationMinDisplaySec}.
 * @param {number} hitCount winning paylines
 * @returns {number}
 */
export function getWinRollupDurationSec(hitCount) {
  const min = Math.max(0.05, Number(gameConfig.winAnimationMinDisplaySec) || 3);
  if (hitCount > 0) return hitCount * min;
  const raw = Number(gameConfig.lastWinRollupDurationSec);
  return Math.max(min, Number.isFinite(raw) && raw > 0 ? raw : min);
}
